import { type OrganizationExternalAuth } from '@koala/sdk';
import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSelector } from '@/redux';
import * as ErrorReporter from '@/utils/errorReporter';
import { tryCatch } from '@/utils/global';

/**
 * TEAM-14519, raised by TEAM-15668: how long `executeRecaptcha` gets, not counting time spent with
 * Google's image puzzle open, before the token fetch counts as failed. Google writes the same
 * 30000 into the `execute-ms` parameter of its own frame.
 */
export const CAPTCHA_TIMEOUT_MS = 30_000;

/**
 * TEAM-15668: the most puzzle time the deadline will hold for. Past it the deadline counts again
 * even if a puzzle still looks open, so a misread page can't hold a caller's submit lock for good.
 */
export const CAPTCHA_MAX_HOLD_MS = 5 * 60_000;

const DEADLINE_TICK_MS = 250;

/** Google's image puzzle renders in its `bframe` iframe (api2 or enterprise, either domain). */
const CHALLENGE_FRAME_SELECTOR = 'iframe[src*="/recaptcha/"][src*="bframe"]';

function isRendered(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (window.getComputedStyle(node).display === 'none') {
      return false;
    }
  }
  // `visibility` inherits, and Google hides a closed puzzle by setting it on the frame's wrapper.
  return window.getComputedStyle(element).visibility !== 'hidden';
}

/**
 * TEAM-15668: whether Google has its image puzzle open on the page. A v2 key driven through the v3
 * API can serve one, and `execute` then stays pending until the diner solves it. Finding nothing
 * (including after a Google markup change) reads as closed, which leaves the plain deadline.
 */
export function isChallengeOpen(): boolean {
  return Array.from(document.querySelectorAll(CHALLENGE_FRAME_SELECTOR)).some(isRendered);
}

/**
 * `grecaptcha.execute` can hang without ever settling (seen in automated Chrome, including on the
 * production site), which would leave a caller's submit lock held for good. Rejects once `ms` has
 * passed with no puzzle open (TEAM-15668: time with one open is held, up to `CAPTCHA_MAX_HOLD_MS`),
 * and clears its timer once `promise` settles; a result that arrives after the deadline is ignored.
 * Measures with the clock rather than counting ticks, since browsers throttle background timers.
 */
function withCaptchaDeadline<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setInterval> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    let last = Date.now();
    let counted = 0;
    let held = 0;
    timer = setInterval(() => {
      const now = Date.now();
      // Wall-clock time can step backwards (NTP, a manual change); never count a negative slice.
      const elapsed = Math.max(0, now - last);
      last = now;
      if (held < CAPTCHA_MAX_HOLD_MS && isChallengeOpen()) {
        held += elapsed;
      } else {
        counted += elapsed;
      }
      if (counted >= ms) {
        reject(new Error(`CAPTCHA execution timed out after ${ms}ms`));
      }
    }, DEADLINE_TICK_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearInterval(timer));
}

/**
 * TEAM-14519: `fetchCaptchaToken` rejects with this on both failure paths instead of resolving
 * `null`, so no caller can mistake a failed fetch for "no captcha needed" and send a request that
 * coo-api can only refuse.
 */
export class CaptchaUnavailableError extends Error {
  readonly reason: 'not_loaded' | 'execution_failed';

  constructor(reason: 'not_loaded' | 'execution_failed') {
    super(`CAPTCHA token unavailable: ${reason}`);
    this.name = 'CaptchaUnavailableError';
    this.reason = reason;
  }
}

/**
 * Whether coo-api will verify a captcha for `action` (TEAM-14519). It only does for actions listed
 * in `external_auth.captcha.actions`, so an empty list requires nothing. With no `actions` at all a
 * site key alone still counts, as before; with no site key no token can be fetched, so nothing is
 * required.
 */
export function isCaptchaRequired(
  captcha: Partial<NonNullable<OrganizationExternalAuth['captcha']>> | undefined,
  action: string,
): boolean {
  if (!captcha?.site_key) {
    return false;
  }
  if (!Array.isArray(captcha.actions)) {
    return true;
  }
  return captcha.actions.includes(action);
}

const useCaptcha = ({ action }: { action: string }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const organizationState = useSelector((state) => state.app.organization);
  const captchaEnabled = isCaptchaRequired(organizationState?.external_auth?.captcha, action);

  const fetchCaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) {
      ErrorReporter.captureMessage('CAPTCHA has not been loaded', 'warning', { action });
      throw new CaptchaUnavailableError('not_loaded');
    }

    // A timeout takes the same branch (and Sentry group, COO-WEBAPP-3NK) as a rejection.
    const [token, error] = await tryCatch(
      withCaptchaDeadline(executeRecaptcha(action), CAPTCHA_TIMEOUT_MS),
    );
    if (error || !token) {
      const errorMessage = new Error(
        'Error executing CAPTCHA. Please refresh the page and try again.',
      );
      ErrorReporter.captureException(errorMessage, 'error', {
        action,
        error,
        isTokenNull: !token ? 'yes' : 'no',
      });
      throw new CaptchaUnavailableError('execution_failed');
    }

    return token;
  }, [executeRecaptcha, action]);

  return {
    fetchCaptchaToken,
    captchaEnabled,
    loadingCaptcha: captchaEnabled && !executeRecaptcha,
  };
};

export default useCaptcha;
